import { getTPayInstallmentChannel } from "@fitness-app/data-models/contants/tpayInstallments";
import { type Order } from "@fitness-app/data-models/entities/Order";

type Translate = (key: string) => string;

/**
 * Flat on purpose. The product-level table reads the whole order and can dig into
 * `metadata`; the global transactions list selects a handful of columns and pulls
 * the two installment keys out of the JSON by path, so it never holds a `metadata`
 * object at all. Both can fill this.
 */
export interface OrderPaymentDescriptor {
  paymentGateway?: string | null;
  paymentMethodType?: string | null;
  selectedPaymentMethod?: string | null;
  /** Comes back as a string when read straight out of the JSON column. */
  tpayInstallmentsChannelId?: number | string | null;
}

export const toOrderPaymentDescriptor = (
  order: Pick<Order, "paymentGateway" | "paymentMethodType" | "metadata">,
): OrderPaymentDescriptor => ({
  paymentGateway: order.paymentGateway,
  paymentMethodType: order.paymentMethodType,
  selectedPaymentMethod: order.metadata?.selectedPaymentMethod as string | null | undefined,
  tpayInstallmentsChannelId: order.metadata?.tpayInstallmentsChannelId as number | string | null | undefined,
});

/**
 * What the payment actually was, split into the gateway and everything that
 * qualifies it.
 *
 * The gateway alone is not enough to reconcile anything: "Tpay" covers a transfer,
 * PayPo and installments alike, and installments additionally differ by bank
 * channel — which is what the Tpay panel lists commission against.
 *
 * Returns parts rather than a sentence so the table can put them on separate lines;
 * as one parenthesised string it wrapped mid-word in a narrow column.
 */
export const describeOrderPayment = (
  order: OrderPaymentDescriptor,
  t: Translate,
): { gateway: string; details: string[] } => {
  const { paymentGateway, paymentMethodType } = order;

  if (!paymentGateway) {
    return { gateway: "-", details: [] };
  }

  const gateway = paymentGateway.toLowerCase();
  const details: string[] = [];

  if (paymentMethodType && paymentMethodType !== gateway && paymentMethodType !== "manualPayment") {
    details.push(t(`productTransactions.paymentMethodType.${paymentMethodType}`));
  }

  if (order.selectedPaymentMethod === "tpay_raty") {
    // Orders placed before installments got their own method type stamped "tpay",
    // so the label above may be missing for them.
    if (!details.length) {
      details.push(t("productTransactions.paymentMethodType.tpay_raty"));
    }

    // The channel's INTERNAL name is the right one here: this is our reconciliation
    // view, and that name is the one the Tpay panel uses.
    const channel = getTPayInstallmentChannel(Number(order.tpayInstallmentsChannelId));

    if (channel) {
      details.push(channel.label);
    }
  }

  return { gateway: t(`productTransactions.paymentGateway.${gateway}`), details };
};
